import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Fab, Paper, Typography, TextField, IconButton, List, ListItem, Avatar, Chip, Tooltip } from '@mui/material';
import { motion, useDragControls } from 'framer-motion';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import api from '../api/axios';

export default function CompiBot() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([
    { text: '¡Hola! Soy **Compi**, tu asistente personal. Escribe **"ayuda"** para ver todo lo que puedo hacer por ti.', isBot: true }
  ]);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const dragControls = useDragControls();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const renderMessage = (text) => {
    // Procesamiento simple de Markdown (negrita, links y saltos de línea)
    return text.split('\n').map((line, i) => (
      <span key={i}>
        {line.split(/(\*\*.*?\*\*|\[.*?\]\(.*?\))/g).map((part, j) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={j}>{part.slice(2, -2)}</strong>;
          }
          if (part.startsWith('[') && part.includes('](')) {
            const match = part.match(/\[(.*?)\]\((.*?)\)/);
            if (match) {
              const label = match[1];
              const url = match[2];
              return (
                <span 
                  key={j} 
                  onClick={() => url.startsWith('/') ? navigate(url) : window.open(url, '_blank')}
                  style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  {label}
                </span>
              );
            }
          }
          return part;
        })}
        {i < text.split('\n').length - 1 && <br />}
      </span>
    ));
  };

  const quickActions = [
    { label: '💰 Ofertas', query: '¿Qué ofertas hay hoy?' },
    { label: '📊 Gastos', query: '¿Cuánto he gastado?' },
    { label: '🛒 Mis listas', query: 'Ver mis listas' },
    { label: '✨ Sorpréndeme', query: 'Sorpréndeme' },
    { label: '❓ Ayuda', query: 'ayuda' },
  ];

  const handleQuickAction = (query) => {
    if (!query.trim() || loading) return;

    // Limpia el input visual
    setInput('');
    // Añade el mensaje del usuario al chat
    setMessages(prev => [...prev, { text: query, isBot: false }]);
    setLoading(true);

    // Llama a la API directamente con la query
    api.get('/compi/ask', { params: { q: query } })
        .then(({ data }) => {
          setMessages(prev => [...prev, { text: data.respuesta, isBot: true }]);
        })
        .catch(() => {
          setMessages(prev => [...prev, { text: 'Lo siento, he tenido un pequeño error.', isBot: true }]);
        })
        .finally(() => setLoading(false));
  };

  const handleSend = async (e) => {
    if (e) e.preventDefault();
    if (!input.trim() || loading) return;
    const query = input.trim();
    setInput('');
    setMessages(prev => [...prev, { text: query, isBot: false }]);
    setLoading(true);
    try {
      const { data } = await api.get('/compi/ask', { params: { q: query } });
      setMessages(prev => [...prev, { text: data.respuesta, isBot: true }]);
    } catch {
      setMessages(prev => [...prev, { text: 'Lo siento, he tenido un pequeño error.', isBot: true }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box 
      component={motion.div}
      drag
      dragControls={dragControls}
      dragListener={false}
      dragMomentum={false}
      sx={{ 
        position: 'fixed', 
        bottom: 20, 
        right: 20, 
        zIndex: 1000,
        touchAction: 'none'
      }}
    >
      {open ? (
        <Paper 
          elevation={6} 
          sx={{ 
            width: { xs: 300, sm: 350 }, 
            height: 450, 
            display: 'flex', 
            flexDirection: 'column',
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <Box 
            onPointerDown={(e) => dragControls.start(e)}
            sx={{ 
              p: 2, 
              bgcolor: 'primary.main', 
              color: 'white', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SmartToyIcon />
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Compi</Typography>
            </Box>
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box 
            ref={scrollRef}
            sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column', gap: 1 }}
          >
            {messages.map((m, idx) => (
              <Box 
                key={idx} 
                sx={{ 
                  alignSelf: m.isBot ? 'flex-start' : 'flex-end',
                  maxWidth: '85%',
                  display: 'flex',
                  gap: 1,
                  flexDirection: m.isBot ? 'row' : 'row-reverse'
                }}
              >
                {m.isBot && <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.light' }}><SmartToyIcon sx={{ fontSize: 16 }} /></Avatar>}
                <Paper 
                  sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    bgcolor: m.isBot ? 'white' : 'primary.main',
                    color: m.isBot ? 'text.primary' : 'white',
                    fontSize: '0.875rem',
                    boxShadow: 1,
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {renderMessage(m.text)}
                </Paper>
              </Box>
            ))}
            {loading && (
              <Box sx={{ alignSelf: 'flex-start', ml: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>Compi está pensando...</Typography>
              </Box>
            )}
          </Box>

          {/* Quick Actions */}
          {!loading && (
            <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1, overflowX: 'auto', bgcolor: '#f5f5f5', '&::-webkit-scrollbar': { display: 'none' } }}>
              {quickActions.map((action, idx) => (
                <Chip 
                  key={idx} 
                  label={action.label} 
                  onClick={() => handleQuickAction(action.query)}
                  size="small"
                  sx={{ bgcolor: 'white', fontWeight: 600, border: '1px solid', borderColor: 'primary.light', '&:hover': { bgcolor: 'primary.light', color: 'white' } }}
                />
              ))}
            </Box>
          )}

          {/* Input */}
          <Box component="form" onSubmit={handleSend} sx={{ p: 2, borderTop: '1px solid #eee', display: 'flex', gap: 1 }}>
            <TextField 
              fullWidth 
              size="small" 
              placeholder="Escribe aquí..." 
              value={input}
              onChange={e => setInput(e.target.value)}
              disabled={loading}
            />
            <IconButton color="primary" type="submit" disabled={!input.trim() || loading}>
              <SendIcon />
            </IconButton>
          </Box>
        </Paper>
      ) : (
        <Tooltip title="¡Muéveme donde quieras!" placement="left">
          <Fab 
            color="primary" 
            aria-label="compi" 
            onPointerDown={(e) => dragControls.start(e)}
            onClick={() => setOpen(true)} 
            sx={{ 
              boxShadow: 4,
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' }
            }}
          >
            <SmartToyIcon />
          </Fab>
        </Tooltip>
      )}
    </Box>
  );
}
